import React from 'react';
import type { RouteComponentProps } from 'react-router';

import { action, computed } from 'mobx';
import { observer } from 'mobx-react';

import { isEmpty } from 'lodash';

import { Button as ZincButton } from '@zinc/ui';
import history from 'bz-utilities/modules/networking/history/rr4';
import { Button as ClickButton, Field } from 'click-controls';
import { TOASTIFY_TYPE } from 'click-controls/constant/enums';
import { LIST_USER_STATUS } from 'click-controls/constant/localData';
import { TOASTIFY_MSG } from 'click-controls/constant/message';
import { Fields } from 'click-controls/controls/Fields';
import ClickForm from 'click-controls/controls/form/generics/Form';
import Spinner from 'click-controls/controls/spinner';
import ToastifyStore from 'click-controls/controls/toastify/toastify-store';

export function ClickOrCoreButton(args) {
  return process.env.isClick ? <ClickButton {...args} /> : <ZincButton {...args} />;
}
const Button = ClickOrCoreButton;
import type { UserSaveRequest } from 'tm3-core/api.dtos';

import AuthStore from 'bz-stores/lib/auth-store';
import { ConfigObservable as Config } from 'bz-stores/lib/config';
import { asInt } from 'bz-utilities/functions/parse';
import UserDialogFormStore from 'modules/settings/controls/setup/controls/users/controls/user-dialog/stores/users-dialog-store';
import { MfaSetupDialog } from 'shell/header/components/user-popover/components/MfaSetupDialog';
import ChangePasswordDialog from './controls/change-password-dialog';
import UserPhotoField from './controls/user-photo';
import UserProfile from './controls/user-profile';

export interface IParams {
  id?: string;
}

export interface IProps extends RouteComponentProps<IParams> {}

const { create, FieldSelect } = new Fields<UserSaveRequest>();
const ClickField = create('ClickField', Field);
const ClickUserPhoto = create('ClickUserPhoto', UserPhotoField);

@observer
class UserSettingsForm extends React.Component<IProps> {
  editing: boolean = false;
  store: UserDialogFormStore;

  constructor(props: IProps) {
    super(props);
    const id = this.props.match?.params?.id ?? AuthStore.user.id;
    this.store = new UserDialogFormStore(id);

    this.store.autoSelectLocation = true;
    if (id) {
      this.editing = true;
    }
  }

  componentWillUnmount() {
    this.editing = false;
  }

  @action.bound
  onNavigateTo(path: string) {
    history.push(path);
  }

  @computed get canChangePassword() {
    return asInt(AuthStore.user.id) === asInt(this.store.get('id')) && !AuthStore.user.hasPassKey;
  }

  showEditName = (firstName: string, lastName: string) => {
    return !!(firstName && lastName)
      ? `${firstName} ${lastName}`
      : firstName && lastName
        ? 'loading...'
        : 'Edit User';
  };

  getBase64 = (img: any, callback: any) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => callback(reader.result));
    reader.readAsDataURL(img);
  };

  validFileSize = (fileSize: number) => {
    const KB = Math.round(fileSize / 1024);
    if (KB > 200) return false;
    else return true;
  };

  validExtension = (fileName: string) => {
    const allowedExtensions = /(\.jpg|\.png)$/i;
    if (!allowedExtensions.exec(fileName)) return false;
    else return true;
  };

  handlePhotoChange = (file: File) => {
    let { set, setAvatar } = this.store;
    const { setMessage } = ToastifyStore;
    if (!file) {
      set('avatar', file), setAvatar('');
    } else {
      if (this.validExtension(file.name)) {
        if (this.validFileSize(file.size)) {
          this.getBase64(file, (base64: any) => {
            set('avatar', base64);
            base64 && setAvatar(base64);
          });
        } else {
          setMessage(TOASTIFY_TYPE.Error, TOASTIFY_MSG.IMAGE.LIMIT_SIZE_KB(200));
        }
      } else {
        setMessage(TOASTIFY_TYPE.Error, TOASTIFY_MSG.IMAGE.LIMIT_EXTENSION);
      }
    }
  };

  handleUpdateResult = (type: string, error: any) => {
    const { setMessage } = ToastifyStore;
    if (type === TOASTIFY_TYPE.Success) {
      this.store.displayChangePasswordField();
      setMessage(TOASTIFY_TYPE.Success, TOASTIFY_MSG.PASSWORD_UPDATE.SUCCESS);
    } else if (type === TOASTIFY_TYPE.Error) {
      setMessage(
        TOASTIFY_TYPE.Error,
        TOASTIFY_MSG.PASSWORD_UPDATE.FAILED(error.responseStatus.message)
      );
    }
  };

  submit = async () => {
    const { setMessage } = ToastifyStore;
    setMessage(TOASTIFY_TYPE.Info, TOASTIFY_MSG.SAVING);
    const { save, msgError } = this.store;
    const success = await save();
    if (success === false) {
      setMessage(TOASTIFY_TYPE.Error, TOASTIFY_MSG.ERROR);
      return;
    }
    if (isEmpty(msgError) && !success.errorCode) {
      if (!this.props.match?.params?.id)
        setMessage(TOASTIFY_TYPE.Success, TOASTIFY_MSG.USER.CREATE);
      else setMessage(TOASTIFY_TYPE.Success, TOASTIFY_MSG.USER.EDIT);
      if (process.env.isClick) history.push('account/users');
    } else setMessage(TOASTIFY_TYPE.Error, TOASTIFY_MSG.ERROR);
  };

  render() {
    const { canChangePassword } = this;
    const {
      get,
      set,
      avatarLocal,
      hasChanges,
      isChangePassword,
      displayChangePasswordField,
      isLoading,
      isSaving,
      isOwnUser
    } = this.store;
    if (isLoading || isSaving) {
      return <Spinner testId="loading" message="" />;
    }
    const editId = get('id');

    return (
      <ClickForm store={this.store}>
        <div>
          <div className="h-full w-full sm:flex">
            <div className="w-3/5 border-r border-gray-300 p-4">
              <div className="flex items-center justify-center">
                <div className="pt6 mb-4 flex w-96 max-w-full flex-col bg-white px-8 pb-8">
                  <ClickField
                    label="First Name"
                    for="firstName"
                    placeholder="Enter First Name"
                    maxLength={40}
                    required
                  />
                  <ClickField
                    label="Last Name"
                    for="lastName"
                    placeholder="Enter Last Name"
                    maxLength={40}
                    required
                  />
                  <ClickField
                    label="Mobile Number"
                    for="mobileNumber"
                    placeholder="Enter Mobile Number"
                    maxLength={255}
                  />
                  <ClickField
                    label="Email Address"
                    for="email"
                    placeholder="Enter Email Address"
                    maxLength={255}
                    required
                  />
                  {!!this.store.msgError && (
                    <p className="-mt-2 text-xs font-medium text-red-500">{this.store.msgError}</p>
                  )}
                  {editId && (
                    <FieldSelect
                      for="current"
                      label="Status"
                      items={LIST_USER_STATUS}
                      displayMember="name"
                      valueMember="value"
                      disabled={editId ? false : true}
                    />
                  )}
                  <ClickUserPhoto
                    className="mt-5 mb-9"
                    label="Photo"
                    for="avatar"
                    onChange={this.handlePhotoChange}
                    accept="image/*"
                    id={editId}
                    value={get('avatar') ?? avatarLocal}
                  />

                  {canChangePassword && isChangePassword && (
                    <ChangePasswordDialog
                      openDialog={isChangePassword}
                      handleUpdateResult={this.handleUpdateResult.bind(this)}
                      store={this.store}
                      isEditMode={true}
                    />
                  )}
                  {editId && (
                    <div className="ml-4 flex flex-col gap-2">
                      <h3 className="text-base font-bold">Security Actions</h3>
                      {canChangePassword ? (
                        <a onClick={() => displayChangePasswordField()}>Change my Password</a>
                      ) : (
                        <a onClick={this.store?.onResetPassword}>Reset Password</a>
                      )}
                      {Config.TwoFactorEnabled && (
                        <>
                          {this.store?.isOwnUser && !AuthStore.user.twoFactorSetup ? (
                            <a onClick={this.store?.onSetupMFA}>
                              Setup User Multi Factor Authentication
                            </a>
                          ) : (
                            <a onClick={this.store?.onResetMFA}>
                              Reset User Multi Factor Authentication
                            </a>
                          )}
                          <a onClick={this.store?.onResetPassKey}>Setup a Pass Key</a>
                        </>
                      )}
                      <MfaSetupDialog
                        onChange={(mode: MfaState) => (this.store.mfaMode = mode)}
                        mode={this.store?.mfaMode}
                        open={this.store?.showMfa}
                        onRequestClose={() => (this.store.showMfa = false)}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="w-2/5">
              <UserProfile />
            </div>
          </div>

          <div className="click-sticky-dialog-footer">
            <Button
              className="btn"
              onClick={this.submit.bind(this)}
              color={hasChanges ? 'primary' : 'neurtal'}
            >
              Save
            </Button>
            {process.env.isClick && (
              <Button className="btn ml-3" onClick={this.onNavigateTo.bind(this, '/account/users')}>
                Cancel
              </Button>
            )}
          </div>
        </div>
      </ClickForm>
    );
  }
}

export default UserSettingsForm;
